import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { useWorstAlarm } from "@/hooks/useWorstAlarm";
import { useIncidentDismissed } from "@/hooks/useIncidentDismissed";
import { carryTo } from "@/lib/carryParams";
import { useT } from "@/lib/i18n";

/**
 * Shell-level critical-incident strip (F24). A slim, full-width row pinned
 * below the topbar on every route EXCEPT /overview (where HealthBanner
 * already carries the same worst-alarm content — promoting it here too would
 * be a duplicate critical surface). Self-suppresses to null in the common
 * no-critical case, so mounting it unconditionally is free.
 *
 * Shares useWorstAlarm() with HealthBanner so the two surfaces can never cite
 * different incidents. Gated on criticalActive (a live active critical
 * alarm) — NOT on snap.health.overall — so it appears only for a real
 * incident, never for HealthBanner's zero-active "reported degraded" path.
 *
 * Per-incident dismissal: the dismissed alarm id is remembered in a tiny
 * shared store (useIncidentDismissed, R09) — NOT component state — so the
 * ShellVerdict can read it and keep showing the in-flow verdict when the strip
 * is dismissed (otherwise dismissing the strip nuked both surfaces). Dismissing
 * hides THIS incident but a NEW worst-critical (a different top.id) re-shows
 * the strip — an operator can't permanently mute a future, possibly worse,
 * incident.
 *
 * `fixed` + in-flow spacer mirrors SimulatedDataBanner / TelemetryOutageStrip
 * in ConnectingSplash (those occupy top-12 / top-20; this is the critical
 * shell surface and is mounted on non-Overview routes where those are
 * typically absent). aria-live="polite" (paired with role="status") rather
 * than assertive: the assertive critical Toaster (useAlarmWatcher, G23)
 * already fires for a new critical, so a polite persistent surface avoids
 * double-announcing while still being read out.
 */
export function IncidentStrip() {
  const { isError, criticalActive, criticalCount, top } = useWorstAlarm();
  const t = useT();
  const { pathname } = useLocation();
  const dismissedId = useIncidentDismissed((s) => s.dismissedId);
  const setDismissedId = useIncidentDismissed((s) => s.setDismissedId);

  if (isError) return null;
  if (!criticalActive) return null;
  if (pathname.startsWith("/overview")) return null;
  if (top && dismissedId === top.id) return null;

  const headline = t(
    criticalCount === 1 ? "monitor.incident.banner.one" : "monitor.incident.banner.other",
    { n: criticalCount },
  );

  return (
    <>
      <div aria-hidden className="h-8" />
      <div
        role="status"
        aria-live="polite"
        className="incident-strip fixed inset-x-0 top-12 z-20 flex h-8 items-center gap-2 border-b border-critical/30 bg-critical/10 px-4 text-critical backdrop-blur-sm print:hidden sm:px-6 lg:px-8"
      >
        <ShieldAlert size={14} className="shrink-0" />
        <span className="flex-1 truncate text-[11px] font-medium">
          {headline}
          {top ? <> · {top.device_name} — {top.message}</> : null}
        </span>
        <Link
          to={carryTo("/alarms", { severity: "critical" })}
          className="flex shrink-0 items-center gap-1 rounded border border-critical/40 bg-critical/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-critical hover:bg-critical/25"
        >
          {t("common.openAlarms")}
        </Link>
        <button
          type="button"
          onClick={() => setDismissedId(top?.id ?? null)}
          aria-label={t("common.dismiss")}
          className="shrink-0 rounded p-0.5 text-critical/70 hover:text-critical"
        >
          <X size={13} />
        </button>
      </div>
    </>
  );
}
